import { useState } from "react";
import { useTranslation } from "react-i18next";
import notify from "devextreme/ui/notify";
import "../../support/messages/TicketPage.css";

// Genel ayarlar — organizasyon, ticket varsayilanlari, bildirimler ve calisma saatleri.
// Backend gelene kadar yerel state'te tutulur; Kaydet sadece bildirim gosterir.

type Settings = {
    companyName: string;
    supportEmail: string;
    timezone: string;
    language: string;
    defaultPriority: string;
    autoAssign: boolean;
    emailNotifications: boolean;
    desktopNotifications: boolean;
    soundNotifications: boolean;
    workStart: string;
    workEnd: string;
    weekendOpen: boolean;
};

const DEFAULTS: Settings = {
    companyName: "mobi Office GmbH",
    supportEmail: "support@mobi.de",
    timezone: "Europe/Berlin",
    language: "de",
    defaultPriority: "2",
    autoAssign: true,
    emailNotifications: true,
    desktopNotifications: false,
    soundNotifications: true,
    workStart: "09:00",
    workEnd: "18:00",
    weekendOpen: false,
};

export default function GeneralSettingsPage() {
    const { t } = useTranslation();
    const [s, setS] = useState<Settings>(DEFAULTS);
    const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setS(prev => ({ ...prev, [k]: v }));

    const save = () => notify(t("settingsSaved"), "success", 2000);

    return (
        <div className="ticket-page-root flex flex-col h-full w-full" style={{ paddingBottom: 30 }}>
            <div className="flex-1 min-h-0" style={{ overflowY: "auto", padding: 20, color: "var(--base-text-color)" }}>
                <div style={{ maxWidth: 720 }}>
                    {/* Organizasyon */}
                    <Card title={t("organization")}>
                        <Field label={t("companyName")}>
                            <TextInput value={s.companyName} onChange={v => set("companyName", v)} />
                        </Field>
                        <Field label={t("supportEmail")}>
                            <TextInput value={s.supportEmail} onChange={v => set("supportEmail", v)} type="email" />
                        </Field>
                        <Field label={t("timezone")}>
                            <Select value={s.timezone} onChange={v => set("timezone", v)} options={[
                                { value: "Europe/Berlin", label: "Europe/Berlin (UTC+1)" },
                                { value: "Europe/Istanbul", label: "Europe/Istanbul (UTC+3)" },
                                { value: "UTC", label: "UTC" },
                            ]} />
                        </Field>
                        <Field label={t("language")} last>
                            <Select value={s.language} onChange={v => set("language", v)} options={[
                                { value: "de", label: "Deutsch" },
                                { value: "en", label: "English" },
                                { value: "tr", label: "Türkçe" },
                            ]} />
                        </Field>
                    </Card>

                    {/* Ticket varsayilanlari */}
                    <Card title={t("ticketing")}>
                        <Field label={t("defaultPriority")}>
                            <Select value={s.defaultPriority} onChange={v => set("defaultPriority", v)} options={[
                                { value: "1", label: t("priorityHigh") },
                                { value: "2", label: t("priorityNormal") },
                                { value: "3", label: t("priorityLow") },
                            ]} />
                        </Field>
                        <Toggle label={t("autoAssign")} desc={t("autoAssignDesc")} value={s.autoAssign} onChange={v => set("autoAssign", v)} last />
                    </Card>

                    {/* Bildirimler */}
                    <Card title={t("notifications")}>
                        <Toggle label={t("emailNotifications")} value={s.emailNotifications} onChange={v => set("emailNotifications", v)} />
                        <Toggle label={t("desktopNotifications")} value={s.desktopNotifications} onChange={v => set("desktopNotifications", v)} />
                        <Toggle label={t("soundNotifications")} value={s.soundNotifications} onChange={v => set("soundNotifications", v)} last />
                    </Card>

                    {/* Calisma saatleri */}
                    <Card title={t("businessHours")}>
                        <Field label={t("openHours")}>
                            <div className="flex items-center gap-2">
                                <TextInput value={s.workStart} onChange={v => set("workStart", v)} type="time" width={120} />
                                <span style={{ color: "var(--subtitle-text-color)" }}>–</span>
                                <TextInput value={s.workEnd} onChange={v => set("workEnd", v)} type="time" width={120} />
                            </div>
                        </Field>
                        <Toggle label={t("weekendOpen")} value={s.weekendOpen} onChange={v => set("weekendOpen", v)} last />
                    </Card>

                    <div className="flex justify-end gap-2" style={{ marginTop: 4 }}>
                        <button type="button" onClick={() => setS(DEFAULTS)}
                            style={{ padding: "8px 16px", borderRadius: 8, fontWeight: 600, border: "1px solid var(--border-color)", background: "transparent", color: "var(--base-text-color)" }}>
                            {t("reset")}
                        </button>
                        <button type="button" onClick={save}
                            className="dx-button dx-button-mode-contained dx-button-default"
                            style={{ padding: "8px 20px", borderRadius: 8, fontWeight: 600 }}>
                            {t("saveChanges")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <div className="text-xs font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 6 }}>
                {title}
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                {children}
            </div>
        </div>
    );
}

function Field({ label, children, last }: { label: string; children: React.ReactNode; last?: boolean }) {
    return (
        <div className="flex items-center justify-between gap-3"
            style={{ padding: "12px 14px", borderBottom: last ? "none" : "1px solid var(--border-color)" }}>
            <span className="text-sm shrink-0" style={{ color: "var(--subtitle-text-color)" }}>{label}</span>
            <div style={{ textAlign: "right" }}>{children}</div>
        </div>
    );
}

function Toggle({ label, desc, value, onChange, last }: { label: string; desc?: string; value: boolean; onChange: (v: boolean) => void; last?: boolean }) {
    return (
        <div className="flex items-center justify-between gap-3"
            style={{ padding: "12px 14px", borderBottom: last ? "none" : "1px solid var(--border-color)" }}>
            <div className="min-w-0">
                <div className="text-sm">{label}</div>
                {desc && <div className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>{desc}</div>}
            </div>
            <button type="button" role="switch" aria-checked={value} onClick={() => onChange(!value)}
                style={{
                    width: 40, height: 22, borderRadius: 999, flexShrink: 0, position: "relative",
                    background: value ? "var(--accent-color, #6366F1)" : "var(--base-bg-darken-4)",
                    transition: "background .15s",
                }}>
                <span style={{
                    position: "absolute", top: 2, left: value ? 20 : 2, width: 18, height: 18, borderRadius: "50%",
                    background: "#fff", transition: "left .15s", boxShadow: "0 1px 2px rgba(0,0,0,.2)",
                }} />
            </button>
        </div>
    );
}

function TextInput({ value, onChange, type = "text", width }: { value: string; onChange: (v: string) => void; type?: string; width?: number }) {
    return (
        <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{
                width: width ?? 260, padding: "6px 10px", borderRadius: 8, fontSize: 14,
                border: "1px solid var(--border-color)", background: "var(--background-color)", color: "var(--base-text-color)",
            }}
        />
    );
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
    return (
        <select
            value={value}
            onChange={e => onChange(e.target.value)}
            style={{
                width: 260, padding: "6px 10px", borderRadius: 8, fontSize: 14,
                border: "1px solid var(--border-color)", background: "var(--background-color)", color: "var(--base-text-color)",
            }}
        >
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}
